import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import CsvSeriesChart from './CsvSeriesChart';

export interface BlogPost {
  slug: string;
  date: string;
  category: string;
  title: string;
  description: string;
  legacyPath: string;
  content: ReactNode;
}

const paragraph = 'text-metal-light leading-relaxed';
const heading3 = "text-lg font-semibold text-white font-['Exo']";

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-white font-['Exo']">{title}</h2>
    {children}
  </section>
);

const Note = ({ children }: { children: ReactNode }) => (
  <div className="static-card p-6 space-y-2">{children}</div>
);

const Signature = () => (
  <p className="text-metal-light text-sm font-['Roboto_Mono'] text-center pt-8 border-t border-metal">
    — せきそう NAG ノート 管理人
  </p>
);

const patternRuns = {
  p1: [
    '/bending/csv/ELEGOO_PLA_Yellow_pattern1_old.csv',
    '/bending/csv/ELEGOO_PLA_Yellow_pattern1_2_old.csv',
    '/bending/csv/ELEGOO_PLA_Yellow_pattern1_3_old.csv'
  ],
  p2: [
    '/bending/csv/ELEGOO_PLA_Yellow_pattern2_old.csv',
    '/bending/csv/ELEGOO_PLA_Yellow_pattern2_2_old.csv',
    '/bending/csv/ELEGOO_PLA_Yellow_pattern2_3_old.csv'
  ],
  p3: [
    '/bending/csv/ELEGOO_PLA_Yellow_pattern3_old.csv',
    '/bending/csv/ELEGOO_PLA_Yellow_pattern3_2_old.csv',
    '/bending/csv/ELEGOO_PLA_Yellow_pattern3_3_old.csv'
  ]
};

const runSeries = (paths: string[]) =>
  paths.map((path, index) => ({ name: `${index + 1}回目`, path }));

export const blogPosts: BlogPost[] = [
  {
    slug: 'bending-test-machine-rebuild',
    date: '2026-03-07',
    category: 'Test Report',
    title: '曲げ試験機を作り直しました｜モーター強化＆ボールねじ化で再スタート',
    description: '久々の更新。前回の脱調問題を解決するため、曲げ試験機を作り直しました。モーター強化とボールねじ化で精度向上を狙います。PLAで前回データと比較したところ、最大応力は同等、ひずみ値に変化が見られました。',
    legacyPath: '/blog/posts/20260307_bending-test-machine-rebuild.html',
    content: (
      <>
        <Section title="はじめに">
          <p className={paragraph}>久々の更新になってしまいました。</p>
          <p className={paragraph}>
            <Link to="/blog/bending-test-first-attempt" className="text-accent hover:text-white underline">前回の初回測定</Link>
            では、80N付近でモーターが脱調してしまう問題がありました。そこで今回、試験機を一から作り直すことにしました。
          </p>
          <p className={paragraph}>主な変更点は、モーターの強化とボールねじの導入です。これで安定した測定ができるようになったと思います。</p>
        </Section>
        <Section title="作り直しで変えたところ">
          <p className={paragraph}>今回の改良で変更した主なポイントは以下の通りです。</p>
          <Note>
            <h3 className={heading3}>今回の変更点まとめ</h3>
            <ul className="text-metal-light text-sm space-y-2">
              <li><strong className="text-white">モーター強化（脱調対策）</strong><br />前回使っていたモーターよりもかなり余裕のあるスペックに変更しました。これで脱調の心配はなくなったはずです。</li>
              <li><strong className="text-white">ボールねじ化（剛性/再現性/位置決め精度の改善狙い）</strong><br />リードシャフトからボールねじに変更しました。寸法精度がかなり上がると思います。剛性も上がっているので、測定の再現性も良くなっているかもしれません。</li>
              <li><strong className="text-white">寸法測定装置（未導入、将来の拡張）</strong><br />寸法測定装置も購入しました。気が向いたら追加するかもしれませんが、今はまだ未導入です。</li>
            </ul>
          </Note>
          <p className={paragraph}>ボールねじ化は特に大きな変更で、位置決め精度と剛性の両方が改善されているはずです。前回のリードシャフトと比べて、どれくらい違いが出るか楽しみです。</p>
        </Section>
        <Section title="さっそくテスト">
          <p className={paragraph}>早速、PLAで前回データと比較してみました。</p>
          <p className={paragraph}>比較に使ったのは、<strong className="text-white">ELEGOO PLA（黄色）のパターン1</strong>です。前回の初回測定で使ったのと同じフィラメント、同じ積層パターンで試験片を作成し、同じ条件で測定しました。これで、試験機の違いによる影響を純粋に比較できるはずです。</p>
          <p className={paragraph}>旧データは前回の試験機（リードシャフト＋旧モーター）で測定したもの、今回データは作り直した試験機（ボールねじ＋強化モーター）で測定したものです。まずはグラフで見てみましょう。</p>
          <h3 className={heading3}>旧データ vs 今回データ 比較グラフ</h3>
          <CsvSeriesChart
            series={[
              { name: '今回データ', path: '/bending/csv/ELEGOO_PLA_Yellow_pattern1.csv' },
              { name: '旧データ', path: '/bending/csv/ELEGOO_PLA_Yellow_pattern1_old.csv' }
            ]}
          />
          <p className={paragraph}>グラフを見ると、いくつか興味深い点が見えてきます。</p>
          <p className={paragraph}>まず、<strong className="text-white">最大応力（Stress）は同じくらいのデータが取れました</strong>。これは良い兆候だと思います。前回と同等の値が出るということは、測定自体は正しく行えているということかもしれません。試験機を作り直しても、最大荷重の測定値が大きく変わらなかったのは安心材料です。</p>
          <p className={paragraph}>一方で、<strong className="text-white">ひずみ値（Strain）は少し前と違うみたい</strong>です。前回よりも若干小さめの値になっています。同じ最大応力に達するまでの変形量が、今回の方が少なくなっているように見えます。</p>
          <p className={paragraph}>この違いが何を意味するのか、まだ断定はできませんが、いくつか考えられる要因があります。ボールねじ化による剛性向上で試験機自体の変形が減ったこと、位置決め精度が上がったことなどが影響している可能性があります。</p>
          <p className={paragraph}>まずは比較の土台ができたので、この調子で他のパターンや他の材料でもデータを取っていきたいと思います。</p>
          <p className={paragraph}>前回は80N付近でステッピングモーターの脱調が起きていましたが、今回のPLAテストでは最大荷重が約127Nまで上がりました。脱調する様子はなく、現時点では改善できていそうです。</p>
          <p className={paragraph}>なお、前回よく出てきた「80N付近」という表現は、今回のグラフ（Stress表示）で見るとおおよそ48MPa付近に相当するポイントのことです。単位が混在して分かりづらくてすみません（荷重Nと応力MPaが混ざっていました）。</p>
        </Section>
        <Section title="今のところの感触">
          <p className={paragraph}>ひずみ値が変わった理由について、いくつか考えられることがあります。</p>
          <p className={paragraph}>まず、<strong className="text-white">剛性が上がったこと</strong>。ボールねじ化で試験機全体の剛性が向上したため、試験片以外の部分の変形が減った可能性があります。これにより、より正確な変位が測定できているのかもしれません。</p>
          <p className={paragraph}>次に、<strong className="text-white">ボールねじ化で寸法精度が上がったこと</strong>。位置決め精度が向上したことで、変位の測定がより正確になっている可能性もあります。</p>
          <p className={paragraph}>真偽は不明ですが、個人的には<strong className="text-white">データ精度が上がったと考えて喜んでいます</strong>。前回よりも信頼できる測定ができるようになったと思いたいです。</p>
        </Section>
        <Section title="これから">
          <p className={paragraph}>ただ、問題もあります。<strong className="text-white">データ取り直しは大変</strong>です。</p>
          <p className={paragraph}>試験機を作り直したことで、前回のデータとの互換性が微妙になってしまいました。理想を言えば、全部のフィラメントで測定し直したいところですが、時間も材料もかかります。</p>
          <p className={paragraph}>それでも、<strong className="text-white">少しずつデータ更新していきたい</strong>と思っています。まずは主要なフィラメントから順番に測定し直して、サイト内のデータを更新していく予定です。</p>
          <Note>
            <h3 className={heading3}>次にやること</h3>
            <p className="text-metal-light text-sm">まずは他の素材でも再測定を進めていきます。PLA以外のフィラメント（PETG、ABS、PA-CFなど）でも同様の傾向が見られるか確認したいです。</p>
            <p className="text-metal-light text-sm">また、測定条件を固定して、代表的なパターンでのデータ取得を優先していく予定です。全パターンを測定するのは大変なので、まずは比較しやすい条件に絞って進めます。</p>
          </Note>
          <p className={paragraph}>今後もよろしくお願いします。</p>
        </Section>
        <Signature />
      </>
    )
  },
  {
    slug: 'bending-test-first-attempt',
    date: '2026-01-09',
    category: 'Test Report',
    title: '自作曲げ試験機で初回測定してみた(ISO178参考)｜80Nで脱調→次回モーター強化で再挑戦',
    description: '自作曲げ試験機での初回測定レポート。ISO178参考の3点曲げ試験で試験片を測定したが、80N付近でステッピングモーター脱調。途中データの比較グラフと次回への改善計画。',
    legacyPath: '/blog/posts/20260109_bending-test-first-attempt.html',
    content: (
      <>
        <Section title="概要">
          <p className={paragraph}>自作の曲げ試験機を使って、初めての測定を行いました。ISO178参考の3点曲げ試験で、目的はサイト内での相対比較です。</p>
          <p className={paragraph}>今回は初回測定なので、まず動かしてデータ取りの流れを作る回です。</p>
          <h3 className={heading3}>試験条件</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-metal-light border border-metal">
              <thead className="bg-metal text-white">
                <tr>
                  <th className="text-left px-4 py-2 font-['Exo']">項目</th>
                  <th className="text-left px-4 py-2 font-['Exo']">値</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['スパン(L)', '64mm'],
                  ['支点径', 'φ10mm(丸棒/ローラー)'],
                  ['押し治具径', 'φ10mm'],
                  ['試験速度', '2mm/min'],
                  ['変位計測', 'ステッピングモーター回転＋シャフト軸から計算'],
                  ['試験環境', '未調整(物置のような部屋)']
                ].map(([label, value]) => (
                  <tr key={label} className="border-t border-metal">
                    <td className="px-4 py-2">{label}</td>
                    <td className="px-4 py-2">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
        <Section title="試験片の印刷(3パターン)">
          <Note>
            <p className="text-metal-light text-sm"><strong className="text-white">使用フィラメント：</strong>ELEGOO PLA（黄色）</p>
            <p className="text-sm">
              <a href="https://elegoo.sjv.io/N91GYV" target="_blank" rel="noopener noreferrer sponsored" className="text-accent hover:text-white underline">ELEGOO公式サイト</a>
            </p>
            <p className="text-metal-light text-xs">※アフィリエイトリンクを含みます</p>
          </Note>
          <p className={paragraph}>ISO178を参考にした試験片寸法は <strong className="text-white">4×10×80mm</strong>（幅b=10mm、厚さd=4mm）です。</p>
          <p className={paragraph}>今回は積層方向の違いによる強度差を見るため、3つの印刷パターンで試験片を作成しました。</p>
          <ul className="text-metal-light space-y-2 list-disc pl-5">
            <li><strong className="text-white">パターン1:</strong> 長手方向に対してレイヤー方向を斜めに配置</li>
            <li><strong className="text-white">パターン2:</strong> 長手方向を垂直にして印刷</li>
            <li><strong className="text-white">パターン3:</strong> 長手方向とレイヤー方向が同一直線</li>
          </ul>
        </Section>
        <Section title="測定してみた結果">
          <p className={paragraph}>試験を開始したところ、荷重が約80Nに達した付近でステッピングモーターに負荷がかかりましたが、外部から負荷を補助することで、最大荷重（Fmax）までデータを取得することができました。</p>
          <p className={paragraph}>今回は<strong className="text-white">パターン1〜3を各3回測定（合計9回）</strong>しました。</p>
        </Section>
        <Section title="実測データ(グラフ)">
          <p className={paragraph}>各パターンの測定結果をグラフで示します。</p>
          <h3 className={heading3}>パターン1（3回重ね）</h3>
          <CsvSeriesChart series={runSeries(patternRuns.p1)} />
          <h3 className={heading3}>パターン2（3回重ね）</h3>
          <CsvSeriesChart series={runSeries(patternRuns.p2)} />
          <h3 className={heading3}>パターン3（3回重ね）</h3>
          <CsvSeriesChart series={runSeries(patternRuns.p3)} />
          <h3 className={heading3}>パターン別比較（代表1本ずつ）</h3>
          <CsvSeriesChart
            series={[
              { name: 'パターン1', path: patternRuns.p1[0] },
              { name: 'パターン2', path: patternRuns.p2[0] },
              { name: 'パターン3', path: patternRuns.p3[0] }
            ]}
          />
          <Note>
            <p className="text-metal-light text-sm"><strong className="text-white">注記:</strong></p>
            <ul className="text-metal-light text-sm space-y-1">
              <li>各パターン3回ずつ測定しています</li>
              <li>外部補助により最大荷重までデータを取得できています</li>
            </ul>
          </Note>
        </Section>
        <Section title="今回わかったこと">
          <p className={paragraph}>初回測定でいくつか重要なことがわかりました。</p>
          <ul className="text-metal-light space-y-2 list-disc pl-5">
            <li><strong className="text-white">パターン2は繰り返し差が大きい:</strong> 何が影響しているか今後検証が必要です</li>
            <li><strong className="text-white">パターン1と3は似た結果:</strong> 今後はパターン1と2を中心に取得する予定です</li>
          </ul>
          <p className={paragraph}>なお、メーカー公表の曲げ強度が「72±5」に対して、今回の結果（1回目）が「71.8」とかなり近い値でした。まだ検証は必要ですが、思ったより正確に測定できている可能性がありそうです。</p>
        </Section>
        <Section title="今後の予定">
          <p className={paragraph}>今後も継続的に測定を行い、データの精度を高めていく予定です。</p>
          <p className={paragraph}>続報を待っててね!</p>
        </Section>
        <Signature />
      </>
    )
  },
  {
    slug: 'centauri-carbon-review',
    date: '2025-12-14',
    category: 'Review',
    title: 'ELEGOO Centauri Carbon を自宅で使ってみた正直レビュー',
    description: 'ELEGOO Centauri Carbonの詳細レビュー。カーボンファイバー強化フレーム、CoreXY機構、高速印刷性能を実機で検証。',
    legacyPath: '/blog/posts/20251207_centauri-carbon-review.html',
    content: (
      <>
        <Section title="はじめに（このレビューの立ち位置）">
          <p className={paragraph}>これはメーカー提供とかじゃなくて、普通に自宅で使ってる個人購入品の話。あと、これが人生初の3Dプリンター。最初は「とりあえず動けばOK」くらいの気持ちで触り始めた。</p>
          <p className={paragraph}>このサイト自体は、設定メモだけじゃなくて、フィラメントの傾向とか失敗とか、ついでに強度（曲げ強度とか）も見ていく予定なので、キラキラレビューというより「自宅運用のリアル寄り」のメモっぽい内容になると思う。</p>
        </Section>
        <Section title="なぜ Centauri Carbon を選んだのか（理由1位：値段）">
          <p className={paragraph}>結論、選んだ理由の1位は<strong className="text-white">値段</strong>。初3Dプリンターでいきなり高いのはちょっと怖かった。</p>
          <p className={paragraph}>で、フルカバー、CoreXY、造形サイズ…このへんを見ていくと普通に値段が上がるんだけど、その条件のわりにCentauri Carbonが安く見えたので決めた。正直、最初は「これでダメなら勉強代」くらいの気持ちもあった。</p>
        </Section>
        <Section title="設置環境と、自宅運用のリアル（音・匂い・温度）">
          <p className={paragraph}>置いてるのは、クーラーを付けてない荷物置きみたいな部屋。作業部屋というより半分物置。温度管理はガチではやってない。</p>
          <h3 className={heading3}>音について</h3>
          <p className={paragraph}>音はそこそこうるさい。扉を閉めてても外から聞こえてくるレベル。夜は隣の部屋で寝てるけど、動かしてるとわずかに聞こえてくる。寝れないほどではないけど「無音」ではない。</p>
          <Note>
            <p className="text-metal-light text-sm"><strong className="text-white">特に筐体ファンを回すと本当にうるさい。</strong>ただ、自分の運用だと筐体ファンを使う頻度は低めなので、ずっと爆音って感じでもない。</p>
          </Note>
          <h3 className={heading3}>匂いについて</h3>
          <p className={paragraph}>匂いは、フルカバーだけど普通に漏れる。完全密閉で匂いゼロみたいなのは期待しない方がいい。</p>
          <h3 className={heading3}>温度について</h3>
          <p className={paragraph}>温度については、フルカバーなので温度維持できるのはかなり良い。ただ、やっぱり<strong className="text-white">ヒーターはあったほうがいい</strong>と思う。自分は別でヒーターを付けて運用してる。</p>
          <p className={paragraph}>とはいえ上げすぎると寿命縮めそうで怖いので、チャンバー温度は30℃程度に留めてる。</p>
        </Section>
        <Section title="開封〜初回プリント（初心者でも普通にいけた）">
          <figure className="space-y-2">
            <img
              src="https://static.readdy.ai/image/089a0c448f95942c9fd798eb643923f1/7f88c3259654c3a4bdf5a0c27f26b3b7.jpeg"
              alt="ELEGOO Centauri Carbon で造形中のベンチー"
              loading="lazy"
              className="w-full rounded-lg border border-metal"
            />
            <figcaption className="text-metal-light text-sm text-center">造形中の様子（最近撮影したベンチー）</figcaption>
          </figure>
          <p className={paragraph}>初回プリントは成功。最初は記事にするつもりがなくて、その時の写真は撮ってない（悔しい）。代わりに最近撮った造形中の写真を載せる。</p>
          <p className={paragraph}>ELEGOO Slicerを使って、スクレーパー、3DBenchy、背面のゴミ箱を作った。モデルはCentauri Carbonのサイトからダウンロードしたやつ。</p>
          <Note>
            <p className="text-metal-light text-sm">プリンタ内に最初から入ってるデータをそのまま印刷…じゃなくて、自分でスライザーで読み込んでスライスした。ただ、設定はいじらずほぼ初期設定のまま。これでも普通に満足いく仕上がりだったので「あ、これなら普通に使えるな」って安心した。</p>
          </Note>
        </Section>
        <Section title="品質とスピード（とりあえず初期設定でも満足）">
          <p className={paragraph}>細かいチューニングとかはまだこれからだけど、少なくとも最初の段階では「初期設定のままでも普通に満足」って感じ。</p>
          <p className={paragraph}>ベンチーとかスクレーパーみたいな定番を出して、変な崩れ方もしなかったので、最初の一歩としてはかなり良かった。</p>
        </Section>
        <Section title="フィラメント相性（さらっとだけ）">
          <p className={paragraph}>今のところ、ほとんどのフィラメントはヒーター無しでも印刷できてる。PLA、PLA-CF、PETG、PETG-CF、PETG-GF、ASAあたりは触ってる。</p>
          <p className={paragraph}>PMMAも印刷したけど、これはかなり微妙だった…。</p>
          <Note>
            <p className="text-metal-light text-sm">
              強度（曲げ強度など）のデータも順次公開していく予定。同条件で比較できる形にしていきたい。詳しくは
              <Link to="/filament" className="text-accent hover:text-white underline">フィラメントデータ</Link>
              や
              <Link to="/filament/bend-strength-test" className="text-accent hover:text-white underline">曲げ強度試験方法</Link>
              のページをチェックしてください。
            </p>
          </Note>
        </Section>
        <Section title="良かった点">
          <p className={paragraph}>3Dプリンターで造形するだけなら、今のところ大きな問題はない。箱から出してすぐ印刷開始できるし、品質も良いし、フィラメントもだいたい扱える。</p>
          <p className={paragraph}>まず「普通に印刷したい」って目的なら素直な機械だと思う。</p>
        </Section>
        <Section title="気になった点（ヒーター無しのASAは冬がきつい）">
          <h3 className={heading3}>ヒーターが無い問題</h3>
          <p className={paragraph}>やっぱりヒーターが無いのでASAは反りやすい。特に冬は難しい。ただ、「絶対無理」ってほどではなくて、工夫次第でどうとでもなるレベル。</p>
          <p className={paragraph}>ヒーターは無いけど、ヒートベッドを高温にして扉を閉めて放置すれば中を温められる。冬でも30℃程度なら上がる印象。ヒーターがあれば立ち上がり時間を短縮できるし、60℃とかを狙うならヒーターはほぼ必須。</p>
          <h3 className={heading3}>マルチカラーできない</h3>
          <p className={paragraph}>あとマルチカラーできないのも気になる点と言えばそうだけど、自分は単色で困ってない。マルチカラーにしたいなら、部品を分けてプラモデルみたいに組み立てればいい派。</p>
          <Note>
            <p className="text-metal-light text-sm">
              マルチカラー開発が事実上中止になってCC2側に行ったのは正直ちょっと悲しかった。でもクーポンが配布されたので、まあ…うん…って感じ。
              <a href="https://elegoo.sjv.io/BnW6Lx" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-white font-semibold">クーポン申請（Centauri Carbon）</a>
            </p>
          </Note>
        </Section>
        <Section title="関連リンク">
          <ul className="space-y-3 text-sm">
            <li>
              <a href="https://elegoo.sjv.io/dOXABQ" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-white">Centauri Carbon（公式／購入）</a>
              <span className="block text-metal-light text-xs">※アフィリエイトリンク</span>
            </li>
            <li>
              <a href="https://elegoo.sjv.io/rav73D" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-white">マニュアル・モデルデータダウンロード</a>
            </li>
            <li><Link to="/filament" className="text-accent hover:text-white">フィラメントデータ</Link></li>
            <li><Link to="/filament/bend-strength-test" className="text-accent hover:text-white">曲げ強度試験方法</Link></li>
            <li><Link to="/troubleshooting" className="text-accent hover:text-white">トラブル対策</Link></li>
            <li><Link to="/blog" className="text-accent hover:text-white">ブログ一覧</Link></li>
            <li>
              <a href="https://elegoo.sjv.io/BnW6Lx" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-white">クーポン申請（Centauri Carbon）</a>
            </li>
          </ul>
        </Section>
        <Signature />
      </>
    )
  },
  {
    slug: 'site-open',
    date: '2025-12-06',
    category: 'Blog Note',
    title: 'サイト公開のご挨拶',
    description: 'せきそうNAGノートを開設しました。FDM方式3Dプリンターの技術記録を公開していきます。',
    legacyPath: '/blog/posts/20251206_site-open.html',
    content: (
      <>
        <section className="space-y-4">
          <p className={paragraph}>はじめまして。今回、FDM方式3Dプリンターに関する個人的な研究ノート「せきそう NAG ノート」を公開することにしました。このページでは、サイトを立ち上げた経緯や、今後どんな感じで運営していくかを簡単にご紹介させていただきます。</p>
          <p className={paragraph}>このサイトは、企業や団体が運営する情報サイトではなく、あくまで個人が家の一角でこつこつと3Dプリンターを回しながら、試行錯誤の記録を残していく場所です。完璧なデータベースや正解集を目指すものではなく、「うまくいったこと」も「うまくいかなかったこと」も含めて、ありのままを記録していくつもりです。</p>
        </section>
        <Section title="想定している読者の方々">
          <p className={paragraph}>このサイトは主に、<strong className="text-white">すでにFDM方式の3Dプリンターを購入済みの方</strong>を対象にしています。</p>
          <p className={paragraph}>組み立てや初期セットアップは済んでいて、「とりあえず造形はできるけれど、もう少し突っ込んで遊びたい」「設定を詰めて安定させたい」「いろんなフィラメントを試してみたい」といった段階の方々に、少しでもヒントになる情報を提供できればと考えています。</p>
          <p className={paragraph}>自分自身も、まだまだ試行錯誤の真っ最中です。同じところでつまずいている方と情報を共有できたら嬉しいです。</p>
        </Section>
        <Section title="サイトのこれから">
          <p className={paragraph}>当面は、<strong className="text-white">フィラメントデータ（設定・傾向・メモ）</strong>を中心に充実させていく予定です。</p>
          <p className={paragraph}>PLA、PETG、ABSといったベーシックな材料から、カーボン繊維強化（CF）やガラス繊維強化（GF）などのコンポジット材料まで、少しずつ試しながら記録を残していきます。各フィラメントの特性、推奨設定、実際に造形してみた感触、失敗したときの原因と対策など、実体験ベースの情報を積み重ねていくつもりです。</p>
          <p className={paragraph}>正直なところ、「これが絶対正解！」みたいなことは言えません。環境や機材によって結果は変わりますし、自分もまだまだ勉強中です。でも、「こういう設定で試してみたらこうなった」「ここで失敗した」みたいな生の記録が、誰かの参考になればいいなと思っています。</p>
        </Section>
        <Section title="最後に">
          <p className={paragraph}>このサイトは、無理せずゆっくりと更新していくつもりです。完璧な正解集ではありませんが、「同じところでつまずいている人」のヒントや共感になればうれしいです。</p>
          <p className={paragraph}>気長にお付き合いいただければ幸いです。どうぞよろしくお願いいたします。</p>
        </Section>
        <Signature />
      </>
    )
  }
];

export const getBlogPost = (slug: string) => blogPosts.find((post) => post.slug === slug);
